import { isValidCardType } from '@api/lendingAPI/PaymentAPIHelper';
import { logError } from '@iwLogger/IwDataDogLogger';
import { fetchPostRepaymentPaymentMethod } from '@iwoca/lapi-client/edge';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import type { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStateKey } from '../../../../../../iwQueryClient/useStateKey';

type TRegisterDebitCardParams = {
    stripe: Stripe;
    cardNumberElement: StripeCardNumberElement;
    billingPostcode: string;
    cardholderName: string;
};

// Registers a debit card with Stripe for repayment collection, then
// invalidates the funding requirements query.
export function useRegisterDebitCard() {
    const stateKey = useStateKey();
    const queryClient = useQueryClient();
    const location = getCurrentLocation();
    const offerId = getQueryParameter(location, 'offerId');
    const amount = getQueryParameter(location, 'amount');

    return useMutation({
        mutationFn: async ({
            stripe,
            cardNumberElement,
            billingPostcode,
            cardholderName
        }: TRegisterDebitCardParams) => {
            const billingDetails = {
                name: cardholderName,
                address: { postal_code: billingPostcode }
            };

            // Always register as primary; funding requirements has no role toggle.
            // TODO: add a "default payment method" toggle when reused on Profile.
            const { data } = await fetchPostRepaymentPaymentMethod({
                stateKey,
                body: {
                    data: {
                        payment_method_type: 'card',
                        role: 'primary'
                    }
                }
            });

            const { error: paymentMethodError, paymentMethod } =
                await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardNumberElement,
                    billing_details: billingDetails
                });

            if (paymentMethodError) {
                logError('[Stripe] Payment method creation failed', {
                    code: paymentMethodError.code,
                    message: paymentMethodError.message
                });
                throw new Error(
                    paymentMethodError.message ||
                        'Failed to create payment method'
                );
            }

            if (!paymentMethod) {
                throw new Error('Failed to create payment method');
            }

            if (
                // @ts-expect-error - Stripe PaymentMethod type omits `card.funding`
                !isValidCardType(paymentMethod, data.accepted_card_types ?? [])
            ) {
                logError('[Stripe] Payment method is not an accepted type', {
                    acceptedTypes: data.accepted_card_types
                });
                throw new Error('CARD_TYPE_NOT_DEBIT');
            }

            const { error, setupIntent } = await stripe.confirmCardSetup(
                data.secret,
                {
                    payment_method: {
                        card: cardNumberElement,
                        billing_details: billingDetails
                    }
                }
            );

            if (error) {
                logError('[Stripe] Setup intent confirmation failed', {
                    code: error.code,
                    message: error.message,
                    declineCode: error.decline_code
                });
                throw new Error(error.message || 'Failed to confirm card');
            }

            if (!setupIntent) {
                logError('[Stripe] Setup intent missing after confirmation');
                throw new Error('Setup intent not found');
            }

            // confirmCardSetup drives 3DS in Stripe's own modal; any non-succeeded
            // status here is a failure, not a reason to redirect.
            if (setupIntent.status !== 'succeeded') {
                logError('[Stripe] Setup intent did not succeed', {
                    status: setupIntent.status
                });
                throw new Error('Setup intent failed');
            }

            // Refetch the mounted list so the step flips to its completed view.
            await queryClient.refetchQueries({
                queryKey: [
                    'fetchGetFundingRequirementsCreditFacility',
                    stateKey,
                    offerId,
                    amount
                ]
            });

            // Remove the unmounted payment_method query so the completed view
            // fetches fresh card details when it mounts after the redirect.
            queryClient.removeQueries({
                queryKey: ['fetchGetPaymentMethod', stateKey]
            });
        }
    });
}
